import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import ButtonComponent from '../../atoms/button'
import { SpinnerWhite } from '../../atoms/icons/special'

import {
  alertIconVariantOptions,
  alertOrientationVariantOptions,
  alertAlignVariantOptions
} from './constants'

import {
  AlertOverlay,
  AlertBase,
  AlertContentContainer,
  ContentHeaderContainer,
  HeaderContainerTitle,
  ContentDescriptionContainer,
  DescriptionContainerText,
  AlertActionsButtonsContainer,
  AlertLoaderContainer,
  ChildrenContainer
} from './styled-components'

const AlertComponent = ({
  variant,
  width,
  height,
  title,
  description,
  actionButtons = [],
  buttonOrientation = alertOrientationVariantOptions.STACKED,
  buttonAlignment = alertAlignVariantOptions.END,
  isLoading,
  isOpen = true,
  setIsOpen = () => {},
  onClickOutside,
  closeWhenClickOutside = true,
  activeOverlay = false,
  children,
  ...props
}) => {
  const containerRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        closeWhenClickOutside
      ) {
        onClickOutside ? onClickOutside() : setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  if (!isOpen) {
    return null
  }

  return (
    <AlertOverlay activeOverlay={activeOverlay}>
      <AlertBase
        ref={containerRef}
        isOverlay={activeOverlay}
        width={width}
        height={height}
        {...props}
      >
        {isLoading && (
          <AlertLoaderContainer>
            <SpinnerWhite size="icon-xl" />
          </AlertLoaderContainer>
        )}

        <AlertContentContainer>
          <ContentHeaderContainer>
            {title && (
              <>
                <HeaderContainerTitle>{title}</HeaderContainerTitle>
                {alertIconVariantOptions[variant]}
              </>
            )}
          </ContentHeaderContainer>
          <ContentDescriptionContainer>
            {description && (
              <DescriptionContainerText>{description}</DescriptionContainerText>
            )}
          </ContentDescriptionContainer>
        </AlertContentContainer>
        {children && <ChildrenContainer>{children}</ChildrenContainer>}
        <AlertActionsButtonsContainer
          direction={buttonOrientation}
          align={buttonAlignment}
        >
          {actionButtons.map((propsItem, index) => (
            <ButtonComponent key={index} {...propsItem} />
          ))}
        </AlertActionsButtonsContainer>
      </AlertBase>
    </AlertOverlay>
  )
}

AlertComponent.propTypes = {
  variant: PropTypes.string,
  size: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  buttonOrientation: PropTypes.oneOf(
    Object.values(alertOrientationVariantOptions)
  ),
  buttonAlignment: PropTypes.oneOf(Object.values(alertAlignVariantOptions)),
  actionButtons: PropTypes.arrayOf(PropTypes.object).isRequired,
  isLoading: PropTypes.bool,
  onClickOutside: PropTypes.func,
  setIsOpen: PropTypes.func,
  closeWhenClickOutside: PropTypes.bool,
  isOpen: PropTypes.bool,
  activeOverlay: PropTypes.bool,
  children: PropTypes.node
}

AlertComponent.displayName = 'AlertComponent'

export default AlertComponent
